import type { Theme } from '@mui/material/styles';
import type { ChartHeaderData } from '../types';

/**
 * Chart Header Utility Functions
 */

/**
 * Validates if chart header data is valid for display
 * @param data - Chart header data to validate
 * @returns True if data is valid for display
 */
export const isValidChartHeaderData = (data: ChartHeaderData): boolean => {
  return (
    typeof data.currentPnL === 'number' &&
    typeof data.equity === 'number' &&
    typeof data.closedBalance === 'number'
  );
};

/**
 * Determines the color for PnL display based on value
 * @param pnlValue - P&L value
 * @param primaryColor - Primary theme color
 * @param errorColor - Error theme color (for negative values)
 * @returns Color string to use for display
 */
export const getPnLColor = (
  pnlValue: number,
  primaryColor: string,
  errorColor?: string,
): string => {
  if (pnlValue < 0 && errorColor) {
    return errorColor;
  }
  return primaryColor;
};

/**
 * Formats metric values for display with fallbacks
 * @param value - Metric value
 * @param fallback - Fallback value if original is invalid
 * @returns Formatted number or fallback
 */
export const formatMetricValue = (
  value: number | undefined | null,
  fallback = 0,
): number => {
  if (typeof value !== 'number' || isNaN(value)) {
    return fallback;
  }
  return value;
};

/**
 * Gets metric display configuration
 * @param metricType - Type of metric ('pnl', 'equity', 'balance')
 * @param theme - MUI theme object
 * @returns Configuration object for metric display
 */
export const getMetricDisplayConfig = (
  metricType: 'pnl' | 'equity' | 'balance',
  theme: Theme,
) => {
  const baseConfig = {
    textSize: 12,
    labelColor: theme.palette.text.third,
  };

  switch (metricType) {
    case 'pnl':
      return {
        ...baseConfig,
        textColor: theme.palette.primary.main,
      };
    case 'equity':
    case 'balance':
    default:
      return {
        ...baseConfig,
        textColor: theme.palette.text.second,
      };
  }
};
